(() => {
  const FINISHES = new Set(['all', 'gold', 'silver']);
  const FORM = '.collection-filters-form';
  const TRIGGER = '[data-action="open-drawer"][data-drawer-id="collection-filter-drawer"]';

  // Native localization can collapse repeated parameter names. Restore the
  // exact facet values carried through its scalar return URL before rendering.
  let restoredLocalization = false;
  const localizedUrl = new URL(location.href);
  const localizationKey = '_berries_filter_values';

  // Shopify preview redirects collapse repeated query names. Carry exact
  // public facet values through that redirect as one scalar, as localization
  // already does. Ordinary public URLs keep their native parameter format.
  const preservePreviewFacets = url => {
    url.searchParams.delete(localizationKey);
    if (!url.searchParams.has('preview_theme_id')) return url;
    const facets = [...new Set(url.searchParams.keys())]
      .filter(name => /^filter\.[pv]\./.test(name) && !name.startsWith('filter.v.price.'))
      .map(name => [name, [...new Set(url.searchParams.getAll(name))]])
      .filter(([, values]) => values.length > 1 || values.some(value => value.includes(',')));
    if (facets.length && facets.length <= 25 && facets.every(([, values]) =>
      values.length <= 100 && values.every(value => value.length <= 2048))) {
      url.searchParams.set(localizationKey, JSON.stringify(facets));
    }
    return url;
  };

  // Old preview redirects serialized this nested option into a Ruby hash.
  // Recover only the exact supported search prefix shape; never evaluate it.
  const legacyPrefix = localizedUrl.searchParams.get('options')?.match(/^\{"prefix" => "(last|none)"\}$/);
  if (legacyPrefix) {
    localizedUrl.searchParams.delete('options');
    if (!localizedUrl.searchParams.has('options[prefix]')) localizedUrl.searchParams.set('options[prefix]', legacyPrefix[1]);
    history.replaceState(history.state, '', localizedUrl);
  }

  if (localizedUrl.searchParams.has(localizationKey)) {
    try {
      const facets = JSON.parse(localizedUrl.searchParams.get(localizationKey));
      if (Array.isArray(facets) && facets.length <= 25 && facets.every(entry =>
        Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string' &&
        /^filter\.[pv]\./.test(entry[0]) && !entry[0].startsWith('filter.v.price.') &&
        Array.isArray(entry[1]) && entry[1].length <= 100 &&
        entry[1].every(value => typeof value === 'string' && value.length <= 2048)
      )) {
        facets.forEach(([name, values]) => {
          localizedUrl.searchParams.delete(name);
          [...new Set(values)].forEach(value => localizedUrl.searchParams.append(name, value));
        });
        restoredLocalization = facets.length > 0;
      }
    } catch (_) {
      // Invalid external URLs must not prevent ordinary filter initialization.
    }
    preservePreviewFacets(localizedUrl);
    history.replaceState(history.state, '', localizedUrl);
  }

  // One controller owns both Shopify facets and the collection finish selection.
  // Native templates retain their own page layout and navigation. Model identity,
  // however, must be resolved across every Shopify record page before counting.
  class NativeCollectionListing {
    constructor(filters) {
      this.filters = filters;
      this.cache = new Map();
    }

    metadata(section) {
      const node = section.querySelector('[data-native-source-page]');
      if (!node) return null;
      const data = node.dataset;
      const value = { page: Number(data.nativeSourcePage), pages: Number(data.nativeSourcePages), records: Number(data.nativeSourceRecords), total: Number(data.nativeSourceTotal) };
      if (!Object.values(value).every(Number.isInteger) || value.page < 1 || value.pages < value.page || value.pages > 209 || value.records < 0 || value.total < value.records || value.total > 25000) throw new Error('Invalid native listing metadata');
      return value;
    }

    async prepare(url, section, signal) {
      const key = new URL(url);
      key.hash = ''; // Fragment identifies a position, not a different collection catalogue.
      key.searchParams.delete('page');
      key.searchParams.delete('finish_page');
      const requested = Math.max(1, Number(url.searchParams.get('page')) || 1);
      let catalogue = this.cache.get(key.href);
      if (!catalogue) {
        let first = section;
        let meta = this.metadata(first);
        if (!meta) {
          if (first.querySelector('.CollectionInner__Products .ProductItem') || !first.querySelector('.CollectionInner__Products .EmptyState, .CollectionInner__Products .UiState--empty')) throw new Error('Missing native listing source');
          catalogue = { count: 0, pages: [first.cloneNode(true)] };
        } else {
          if (meta.page !== 1) {
            first = await this.filters.fetchSection(key, signal);
            meta = this.metadata(first);
          }
          if (!meta || meta.page !== 1) throw new Error('Missing first native page');
          const fragments = [first];
          let page = 2;
          await Promise.all(Array.from({ length: Math.min(4, meta.pages - 1) }, async () => {
            while (page <= meta.pages) {
              const number = page++;
              const request = new URL(key);
              request.searchParams.set('page', number);
              const fragment = await this.filters.fetchSection(request, signal);
              const current = this.metadata(fragment);
              if (!current || current.page !== number || current.pages !== meta.pages || current.total !== meta.total) throw new Error('Native source changed during pagination');
              fragments[number - 1] = fragment;
            }
          }));
          if (fragments.reduce((total, fragment) => total + this.metadata(fragment).records, 0) !== meta.total) throw new Error('Incomplete native record source');
          const seen = new Set();
          for (const fragment of fragments) {
            for (const cell of fragment.querySelectorAll('.ProductList > .Grid__Cell')) {
              const card = cell.querySelector('.ProductItem');
              if (!card) continue; // Keep the template's editorial blocks.
              const identity = card.querySelector('[data-product-card-finish-pair]')?.dataset.productCardFinishPair || `product:${card.dataset.regiosDoppGenericProductId || card.dataset.handle}`;
              if (!card.dataset.handle) throw new Error('Missing native model identity');
              if (seen.has(identity)) cell.remove();
              else seen.add(identity);
            }
          }
          for (const fragment of fragments) {
            const count = fragment.querySelector('[data-collection-model-count]');
            if (count) count.dataset.collectionModelCount = seen.size;
          }
          catalogue = { count: seen.size, pages: fragments.map(fragment => fragment.cloneNode(true)) };
        }
        if (signal.aborted) throw new Error('Native source request aborted');
        this.cache.set(key.href, catalogue);
      }
      if (catalogue.count === 0) {
        const section = catalogue.pages[0].cloneNode(true);
        const wrapper = section.querySelector('.ProductListWrapper');
        if (wrapper) {
          const state = wrapper.querySelector('template[data-native-empty-state]')?.content.firstElementChild?.cloneNode(true);
          if (!state) throw new Error('Missing native empty-state component');
          let count = wrapper.querySelector('[data-collection-model-count]');
          if (!count) { count = document.createElement('span'); count.hidden = true; }
          count.dataset.collectionModelCount = '0';
          const editorialGrid = wrapper.querySelector('.ProductList')?.cloneNode(false);
          if (editorialGrid) {
            editorialGrid.classList.remove('ProductList--InfiniteScroll');
            editorialGrid.removeAttribute('data-nextpage');
            for (const fragment of catalogue.pages) {
              for (const cell of fragment.querySelectorAll('.ProductList > .Grid__Cell')) {
                if (!cell.querySelector('.ProductItem')) editorialGrid.append(cell.cloneNode(true));
              }
            }
          }
          wrapper.replaceChildren(count, state);
          if (editorialGrid?.children.length) wrapper.append(editorialGrid);
          section.querySelector('.CollectionInner__Products .products__pagination')?.remove();
        }
        url.searchParams.delete('page');
        return { count: 0, pages: [section], section };
      }
      if (requested > catalogue.pages.length) throw new Error('Invalid native listing page');
      const occupied = catalogue.pages.map((section, index) => section.querySelector('.ProductList > .Grid__Cell') ? index : -1).filter(index => index >= 0);
      let selected = requested - 1;
      if (!occupied.includes(selected)) selected = occupied.find(index => index > selected) ?? occupied.at(-1);
      if (selected === undefined) throw new Error('Missing native model page');
      if (selected === 0) url.searchParams.delete('page');
      else url.searchParams.set('page', selected + 1);
      const selectedSection = catalogue.pages[selected].cloneNode(true);
      const pagination = selectedSection.querySelector('.CollectionInner__Products .products__pagination');
      if (pagination && occupied.length < 2) pagination.remove();
      else if (pagination) {
        for (const anchor of pagination.querySelectorAll('a[href]')) {
          const destination = new URL(anchor.getAttribute('href'), url);
          const number = Number(destination.searchParams.get('page') || 1) - 1;
          const relation = anchor.getAttribute('rel');
          const adjacent = relation === 'next' ? occupied.find(index => index > selected) : relation === 'prev' ? occupied.filter(index => index < selected).at(-1) : undefined;
          if (relation === 'next' || relation === 'prev') {
            if (adjacent !== undefined) { destination.searchParams.set('page', adjacent + 1); anchor.href = destination.href; }
            else (anchor.closest('li') || anchor).remove();
          } else if (!occupied.includes(number)) (anchor.closest('li') || anchor).remove();
        }
      }
      return { count: catalogue.count, pages: catalogue.pages, section: selectedSection };
    }

    stop() {
      this.revision = (this.revision || 0) + 1;
      this.observer?.disconnect();
      this.observer = null;
    }

    install(pages) {
      this.stop();
      const revision = this.revision;
      const owner = this.filters;
      const grid = owner.root.querySelector('.ProductList--InfiniteScroll');
      if (!grid || !pages?.length) return;
      let current = this.metadata(owner.root)?.page || 1;
      const observeLast = () => {
        if (this.revision !== revision || !this.observer) return;
        this.observer.disconnect();
        if (!grid.isConnected || current >= pages.length) {
          grid.removeAttribute('data-nextpage');
          return;
        }
        // Hidden counters and editorial cells may follow the last product.
        const cells = [...grid.querySelectorAll(':scope > .Grid__Cell')];
        const last = cells.filter(cell => cell.querySelector('.ProductItem')).at(-1) || cells.at(-1);
        if (last) this.observer.observe(last);
      };
      this.observer = new IntersectionObserver(entries => {
        if (this.revision !== revision || !entries.some(entry => entry.isIntersecting) || !grid.isConnected) return;
        this.observer.disconnect();
        do {
          const fragment = pages[current++];
          const nextGrid = fragment?.querySelector('.ProductList');
          if (!nextGrid) break;
          for (const cell of nextGrid.querySelectorAll(':scope > .Grid__Cell')) grid.append(cell.cloneNode(true));
          const metadata = grid.closest('[data-native-source-page]');
          if (metadata) metadata.dataset.nativeSourcePage = current;
          const next = nextGrid.getAttribute('data-nextpage');
          if (next) grid.setAttribute('data-nextpage', next);
          else grid.removeAttribute('data-nextpage');
          // A page containing only already-seen models must not stop continuation.
          if (nextGrid.querySelector('.ProductItem')) break;
        } while (current < pages.length);
        owner.owner.refreshFilterProducts?.();
        document.dispatchEvent(new CustomEvent('theme:scroll:end', { detail: { page: current } }));
        observeLast();
      }, { rootMargin: '0px 0px 160px 0px', threshold: 0 });
      observeLast();
    }
  }

  class CollectionFilters {
    static scrollToProducts(root) {
      const products = root.dataset.finishMode === 'pops'
        ? root.querySelector('.PopsCollection__group:not([hidden]) .ProductList')
        : root.querySelector('#collection-products, .CollectionInner__Products');
      if (!products) return;
      const toolbar = root.querySelector('[data-collection-filter-toolbar], .CollectionToolbar--top');
      const style = toolbar && getComputedStyle(toolbar);
      const offset = style?.position === 'sticky'
        ? (parseFloat(style.top) || 0) + toolbar.getBoundingClientRect().height : 0;
      const gap = parseFloat(getComputedStyle(root).getPropertyValue('--space-xs')) || 8;
      const top = window.scrollY + products.getBoundingClientRect().top - offset - gap;
      window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
    }

    constructor(owner, root = owner.element, demo = false) {
      this.owner = owner;
      this.root = root;
      this.demo = demo;
      this.mode = demo ? 'demo' : root.dataset.finishMode;
      this.isSearch = root.hasAttribute('data-search-models');
      this.groupFinishes = !demo && (this.mode === 'essentials' || root.hasAttribute('data-collection-models')) && (!this.isSearch || Number(root.dataset.searchTotal) > 0 || Boolean(root.querySelector(FORM)));
      this.nativeListing = !demo && root.hasAttribute('data-native-listing-root') && this.mode !== 'pops' && !this.groupFinishes ? new NativeCollectionListing(this) : null;
      this.parameter = this.mode === 'pops' ? 'pops_finish' : 'collection_finish';
      this.drawer = root.querySelector('#collection-filter-drawer');
      this.committed = new URL(location.href);
      this.cache = new Map();
      this.operation = 0;
      this.openPosition = null;
      this.previewOperation = 0;
      this.onViewportChange = () => this.updateDrawerViewport();
      window.addEventListener('resize', this.onViewportChange);
      this.previewBarObserver = new MutationObserver(records => {
        if (records.some(record => record.target.id === 'PBarNextFrame' ||
          [...record.addedNodes].some(node => node.nodeType === 1 &&
            (node.id === 'PBarNextFrame' || node.querySelector?.('#PBarNextFrame'))))) {
          this.updateDrawerViewport();
        }
      });
      this.previewBarObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'hidden'] });
      this.onClick = this.click.bind(this);
      this.onChange = event => {
        this.change();
        if (event.target.closest(FORM)) this.schedulePreview(event.target.closest(FORM));
      };
      this.onInput = event => {
        if (event.target.closest(FORM) && ['number', 'range'].includes(event.target.type)) this.onChange(event);
      };
      this.onSubmit = this.submit.bind(this);
      this.onKeyDown = event => {
        if (this.drawer?.getAttribute('aria-hidden') !== 'false') return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.owner.filterDrawer.close();
        } else if (event.key === 'Tab') {
          const controls = this.drawerControls();
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (!first) return;
          if (!this.drawer.contains(document.activeElement)) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus({ preventScroll: true });
          } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus({ preventScroll: true });
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus({ preventScroll: true });
          }
        }
      };
      this.onPopState = () => {
        const requested = new URL(location.href);
        if (this.drawer?.getAttribute('aria-hidden') === 'false') {
          this.owner.filterDrawer.close();
          this.drawerChanged();
        }
        this.apply(requested, { history: true });
      };
      root.addEventListener('click', this.onClick, true);
      root.addEventListener('change', this.onChange);
      root.addEventListener('input', this.onInput);
      root.addEventListener('submit', this.onSubmit);
      document.addEventListener('keydown', this.onKeyDown);
      if (!demo) window.addEventListener('popstate', this.onPopState);
      if (this.drawer) {
        this.observer = new MutationObserver(() => this.drawerChanged());
        this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
      }
      root.querySelectorAll(TRIGGER).forEach(button => {
        button.hidden = !this.drawer;
        button.disabled = !this.drawer;
        if (!this.drawer) button.style.setProperty('display', 'none', 'important');
      });
      const initializeResults = () => {
        if (!this.root.isConnected) return;
        // Shopify can finish a native language POST by replacing /localization
        // with the resource URL. Read that final URL before requesting sections.
        if (/(?:^|\/)localization\/?$/.test(this.committed.pathname)) {
          this.committed = new URL(location.href);
        }
        const devPreview = document.querySelector('[data-theme-environment-switcher] [data-theme-preview-id][aria-current="true"]');
        if (devPreview) {
          this.committed.searchParams.set('preview_theme_id', devPreview.dataset.themePreviewId);
        }
        // Retired POPS type links must not keep a selection that cannot be cleared in the UI.
        const retiredPopsType = this.mode === 'pops' && this.committed.searchParams.has('filter.p.product_type');
        if (retiredPopsType) this.committed.searchParams.delete('filter.p.product_type');
        const modelCount = this.root.querySelector('[data-collection-model-count]')?.dataset.collectionModelCount;
        this.sync(this.committed, this.nativeListing || modelCount === undefined ? undefined : Number(modelCount));
        if (this.mode === 'pops') {
          this.sync(this.committed, this.filterPops(this.root, this.committed));
          if (retiredPopsType || restoredLocalization || this.popsNeedsIndependentSets(this.committed)) {
            this.apply(this.committed, { initial: true });
          }
        } else if (this.groupFinishes || this.nativeListing || (restoredLocalization && !demo)) {
          this.apply(this.committed, { initial: true });
        }
      };
      if (/(?:^|\/)localization\/?$/.test(this.committed.pathname) && document.readyState !== 'complete') {
        this.onInitialPageShow = initializeResults;
        window.addEventListener('pageshow', this.onInitialPageShow, { once: true });
      } else {
        initializeResults();
      }
    }

    destroy() {
      this.nativeListing?.stop();
      if (this.onInitialPageShow) window.removeEventListener('pageshow', this.onInitialPageShow);
      this.operation += 1;
      this.cancelPreview();
      cancelAnimationFrame(this.focusFrame);
      this.controller?.abort();
      this.observer?.disconnect();
      this.previewBarObserver.disconnect();
      window.removeEventListener('resize', this.onViewportChange);
      this.root.removeEventListener('click', this.onClick, true);
      this.root.removeEventListener('change', this.onChange);
      this.root.removeEventListener('input', this.onInput);
      this.root.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('popstate', this.onPopState);
    }

    finish(url) {
      const value = url.searchParams.get(this.parameter);
      return FINISHES.has(value) ? value : 'all';
    }

    filterValues(url, name) {
      const knownValues = [...this.root.querySelectorAll('input[name]')]
        .filter(input => input.name === name).map(input => input.value);
      return [...new Set(url.searchParams.getAll(name)
        .flatMap(value => knownValues.includes(value) ? [value] : value.split(','))
        .filter(value => value !== ''))];
    }

    drawerControls() {
      if (!this.drawer) return [];
      const controls = [...this.drawer.querySelectorAll(
        'button:not([disabled]), a[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'
      )].filter(element => element.tabIndex >= 0 && element.getClientRects().length &&
        getComputedStyle(element).visibility !== 'hidden');
      return controls.filter(element => {
        if (element.type !== 'radio' || !element.name) return true;
        const group = controls.filter(control => control.type === 'radio' && control.name === element.name);
        return element === (group.find(control => control.checked) || group[0]);
      });
    }

    restorePosition(position) {
      if (position === null || position === undefined) return;
      const restore = () => window.scrollTo({ top: Math.min(position, Math.max(0,
        document.documentElement.scrollHeight - window.innerHeight)), behavior: 'instant' });
      restore();
      requestAnimationFrame(restore);
    }

    updateDrawerViewport() {
      if (!this.drawer) return;
      const bar = document.getElementById('PBarNextFrame');
      let inset = 0;
      if (bar && this.drawer.getAttribute('aria-hidden') === 'false') {
        // Measure the toolbar's hit region; its iframe can span the whole viewport.
        const rect = this.drawer.getBoundingClientRect();
        const x = Math.max(1, Math.min(window.innerWidth - 1, rect.left + rect.width / 2));
        const limit = Math.min(window.innerHeight / 2, 160);
        for (let depth = 1; depth <= limit; depth += 2) {
          if (document.elementFromPoint(x, window.innerHeight - depth) !== bar) break;
          inset = depth + 2;
        }
      }
      this.drawer.style.setProperty('--filter-preview-inset', `${inset}px`);
    }

    drawerChanged() {
      const open = this.drawer.getAttribute('aria-hidden') === 'false';
      this.updateDrawerViewport();
      this.root.querySelectorAll(TRIGGER).forEach(button => button.setAttribute('aria-expanded', String(open)));
      cancelAnimationFrame(this.focusFrame);
      if (open && !this.drawer.contains(document.activeElement)) {
        // The opening mutation precedes the drawer's visible layout. Retry on
        // animation frames until an actual control can receive focus.
        let frames = 0;
        const focusDrawer = () => {
          if (this.drawer.getAttribute('aria-hidden') !== 'false' ||
            this.drawer.contains(document.activeElement)) return;
          this.drawerControls()[0]?.focus({ preventScroll: true });
          if (!this.drawer.contains(document.activeElement) && frames++ < 30) {
            this.focusFrame = requestAnimationFrame(focusDrawer);
          }
        };
        this.focusFrame = requestAnimationFrame(focusDrawer);
      }
      if (!open) this.cancelPreview();
      if (!open && this.openPosition !== null) {
        if (this.root.getAttribute('aria-busy') === 'true') {
          this.operation += 1;
          this.controller?.abort();
          this.status();
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
        this.sync(this.committed);
        this.root.querySelector(TRIGGER)?.focus({ preventScroll: true });
        this.restorePosition(this.openPosition);
        this.openPosition = null;
      }
    }

    click(event) {
      const trigger = event.target.closest(TRIGGER);
      if (trigger) {
        this.openPosition = window.scrollY;
        this.sync(this.committed);
        this.schedulePreview(this.drawer?.querySelector(FORM));
        return;
      }
      const apply = event.target.closest('[data-action="apply-filters"]');
      const clear = event.target.closest('[data-action="clear-filters"]');
      const page = event.target.closest('[data-finish-page]');
      const nativePage = this.nativeListing && event.target.closest('.products__pagination a[href]');
      if (!apply && !clear && !page && !nativePage) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (apply) {
        if (!apply.disabled) this.apply(this.draft(apply.closest(FORM)), { close: true });
      }
      else if (nativePage) this.apply(new URL(nativePage.href, location.href), { pagination: true });
      else if (page) {
        const url = new URL(location.href);
        url.searchParams.set('finish_page', page.dataset.finishPage);
        url.searchParams.delete('page');
        url.hash = '#collection-products';
        this.apply(url, { pagination: true });
      }
      else if (clear.closest(FORM)) {
        const form = clear.closest(FORM);
        Array.from(form.elements).forEach(input => {
          if (input.name?.startsWith('filter.') || input.name === 'design_system_available') {
            if (input.type === 'checkbox') input.checked = false;
            else input.value = '';
          }
          if (input.name === 'collection_finish') input.checked = input.value === 'all';
        });
        this.syncRanges(form);
        this.change();
        this.schedulePreview(form);
      } else {
        const url = new URL(location.href);
        Array.from(url.searchParams.keys()).forEach(name => {
          if (name.startsWith('filter.') || [this.parameter, 'page', 'finish_page'].includes(name)) url.searchParams.delete(name);
        });
        this.apply(url);
      }
    }

    submit(event) {
      if (!event.target.matches(FORM)) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.changed(event.target) && this.root.getAttribute('aria-busy') !== 'true') {
        this.apply(this.draft(event.target), { close: true });
      }
    }

    draft(form) {
      const url = new URL(location.href);
      Array.from(url.searchParams.keys()).forEach(name => {
        if (name.startsWith('filter.') || ['sort_by', this.parameter, 'page', 'finish_page', 'design_system_available'].includes(name)) url.searchParams.delete(name);
      });
      for (const [name, value] of new FormData(form)) {
        if (name === 'collection_finish') {
          if (FINISHES.has(value) && value !== 'all') url.searchParams.set(this.parameter, value);
        } else if (value !== '' && (name.startsWith('filter.') || ['sort_by', 'q', 'type', 'options[prefix]', 'design_system_available'].includes(name))) {
          if (name.startsWith('filter.')) url.searchParams.append(name, value);
          else url.searchParams.set(name, value);
        }
      }
      return preservePreviewFacets(url);
    }

    selection(url, form) {
      const selected = new URLSearchParams();
      for (const name of new Set(url.searchParams.keys())) {
        const value = url.searchParams.get(name);
        if (value !== '' && (name.startsWith('filter.') ||
          [this.parameter, 'design_system_available'].includes(name))) {
          const values = name.startsWith('filter.') && !name.startsWith('filter.v.price.')
            ? this.filterValues(url, name) : [value];
          values.forEach(selectedValue => {
            if (name !== this.parameter || selectedValue !== 'all') selected.append(name, selectedValue);
          });
        }
      }
      const defaultSort = form.querySelector('[name="sort_by"][checked]')?.value || '';
      selected.set('sort_by', url.searchParams.get('sort_by') || defaultSort);
      return JSON.stringify([...selected].sort(([a, av], [b, bv]) =>
        a.localeCompare(b) || av.localeCompare(bv)));
    }

    changed(form) {
      return this.selection(this.draft(form), form) !== this.selection(this.committed, form);
    }

    change() {
      this.root.querySelectorAll(FORM).forEach(form => {
        const busy = this.root.getAttribute('aria-busy') === 'true';
        form.querySelectorAll('[data-action="apply-filters"]').forEach(button => {
          button.disabled = busy || !this.changed(form);
          button.setAttribute('aria-disabled', String(button.disabled));
        });
        const active = Array.from(form.elements).some(input =>
          input.name?.startsWith('filter.') ? (input.type === 'checkbox' ? input.checked : input.value !== '')
            : input.name === 'collection_finish' ? input.checked && input.value !== 'all'
              : input.name === 'design_system_available' && input.checked);
        form.querySelectorAll('[data-action="clear-filters"]').forEach(clear => {
          clear.disabled = busy || !active;
          clear.setAttribute('aria-disabled', String(clear.disabled));
        });
      });
    }

    syncRanges(form) {
      form.querySelectorAll('price-range').forEach(range => {
        const sliders = range.querySelectorAll('input[type="range"]');
        const fields = range.querySelectorAll('input[type="number"]');
        if (sliders.length !== 2 || fields.length !== 2) return;
        sliders[0].value = fields[0].value || sliders[0].min;
        sliders[1].value = fields[1].value || sliders[1].max;
        sliders[0].parentElement.style.setProperty('--range-min', (Number(sliders[0].value) / Number(sliders[0].max) * 100) + '%');
        sliders[1].parentElement.style.setProperty('--range-max', (Number(sliders[1].value) / Number(sliders[1].max) * 100) + '%');
      });
    }

    sync(url, count) {
      const finish = this.finish(url);
      this.root.querySelectorAll(FORM).forEach(form => {
        Array.from(form.elements).forEach(input => {
          if (input.name?.startsWith('filter.')) {
            if (input.type === 'checkbox') input.checked = this.filterValues(url, input.name).includes(input.value);
            else input.value = url.searchParams.get(input.name) || '';
          } else if (input.name === 'collection_finish') input.checked = input.value === finish;
          else if (input.name === 'sort_by') input.checked = url.searchParams.has('sort_by') ? input.value === url.searchParams.get('sort_by') : input.defaultChecked;
          else if (input.name === 'design_system_available') input.checked = url.searchParams.has(input.name);
        });
        this.syncRanges(form);
      });
      this.root.dataset.selectedFinish = finish;
      const facetNames = [...new Set(url.searchParams.keys())]
        .filter(name => name.startsWith('filter.') && !name.startsWith('filter.v.price.'));
      const priceActive = ['filter.v.price.gte', 'filter.v.price.lte']
        .some(name => Boolean(url.searchParams.get(name)));
      const activeCount = facetNames.reduce((count, name) => count + this.filterValues(url, name).length, 0)
        + Number(priceActive) + Number(finish !== 'all') + Number(url.searchParams.has('design_system_available'));
      this.root.querySelectorAll(TRIGGER).forEach(button => {
        let badge = button.querySelector('.UiFilterTrigger__count');
        if (!badge && activeCount) {
          badge = document.createElement('span');
          badge.className = 'UiFilterTrigger__count';
          button.insertBefore(badge, button.lastElementChild);
        }
        if (badge) {
          badge.textContent = activeCount;
          badge.hidden = activeCount === 0;
        }
      });
      this.root.querySelectorAll('[data-filter-clear]').forEach(button => button.hidden = activeCount === 0);
      this.resultCount(count);
      if (this.isSearch) window.ProductSearchModels.updateCount(this.root, count);
      this.change();
    }

    resultCount(count) {
      const known = Number.isInteger(count) && count >= 0;
      this.root.querySelectorAll('[data-filter-result-count], .UiFilters__actions .UiFilterOption__count').forEach(node => {
        node.hidden = !known;
        node.textContent = known ? '(' + count + ')' : '';
      });
    }

    cancelPreview() {
      this.previewOperation += 1;
      window.clearTimeout(this.previewTimer);
      this.previewController?.abort();
    }

    schedulePreview(form) {
      this.cancelPreview();
      this.resultCount();
      if (!form || !this.changed(form)) return;
      const token = this.previewOperation;
      this.previewTimer = window.setTimeout(() => this.preview(this.draft(form), token), 300);
    }

    async preview(url, token) {
      if (this.demo) {
        this.resultCount(3 - Number(this.finish(url) !== 'all') - Number(url.searchParams.has('design_system_available')));
        return;
      }
      const controller = this.previewController = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const request = new URL(url);
        if (this.groupFinishes) request.searchParams.delete('page');
        let section = await this.fetchSection(request, controller.signal);
        const result = section.querySelector('[data-collection-model-count]') || section.querySelector('[data-filter-result-count]');
        const resultCount = result?.dataset.collectionModelCount ?? result?.dataset.filterResultCount;
        let count = resultCount !== undefined && /^\d+$/.test(resultCount) ? Number(resultCount) : undefined;
        if (this.mode === 'pops') count = this.filterPops(section, url);
        else if (this.groupFinishes) {
          count = this.renderEssentials(section, await this.catalogue(url, section, controller.signal), url);
        } else if (this.nativeListing) {
          count = (await this.nativeListing.prepare(url, section, controller.signal)).count;
        } else {
          const node = section.querySelector('[data-collection-model-count]') || section.querySelector('[data-filter-result-count]');
          count = Number(node?.dataset.collectionModelCount ?? node?.dataset.filterResultCount);
          if (!node || !Number.isInteger(count) || count < 0) throw new Error('Missing results count');
        }
        if (token === this.previewOperation && !controller.signal.aborted) this.resultCount(count);
      } catch (error) {
        // A failed preview must never show the count for an earlier selection.
        if (token === this.previewOperation) this.resultCount();
      } finally {
        window.clearTimeout(timeout);
      }
    }

    status(message = '', busy = false) {
      this.root.setAttribute('aria-busy', String(busy));
      const searchStatus = this.root.querySelector('[data-search-model-status]');
      if (searchStatus) { searchStatus.textContent = message; searchStatus.hidden = !message; }
      this.root.querySelectorAll(FORM).forEach(form => {
        let status = form.querySelector('[data-filter-status]');
        if (!status) {
          status = document.createElement('p');
          status.dataset.filterStatus = '';
          status.className = 'CollectionFilterStatus';
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
          form.querySelector('.UiFilters__actions, .Drawer__Actions')?.before(status);
        }
        status.textContent = message;
        status.hidden = !message;
        form.querySelectorAll('[data-action="apply-filters"]').forEach(button => {
          button.setAttribute('aria-busy', String(busy));
        });
      });
      this.change();
    }

    popsNeedsIndependentSets(url) {
      return [...url.searchParams.keys()].some(key => key.startsWith('filter.'))
        || url.searchParams.has('page')
        || url.searchParams.has('sort_by');
    }

    async fetchSection(url, signal, includePopsSets = true) {
      if (this.mode === 'pops' && includePopsSets && this.popsNeedsIndependentSets(url)) {
        const unfiltered = new URL(url);
        for (const key of [...unfiltered.searchParams.keys()]) {
          if (key.startsWith('filter.') || ['sort_by', 'page', 'finish_page', this.parameter].includes(key)) {
            unfiltered.searchParams.delete(key);
          }
        }
        // Builder results and independent sets must both succeed before Apply changes the page.
        const [section, recommendations] = await Promise.all([
          this.fetchSection(url, signal, false),
          this.fetchSection(unfiltered, signal, false)
        ]);
        const sets = section.querySelector('#pops-sets .ProductList');
        const allSets = recommendations.querySelector('#pops-sets .ProductList');
        if (!sets || !allSets) throw new Error('Missing ready-made POPS sets');
        sets.replaceChildren(...Array.from(allSets.childNodes));
        return section;
      }
      const request = new URL(url);
      if (request.origin !== location.origin || request.pathname !== this.committed.pathname) throw new Error('Invalid collection URL');
      request.searchParams.delete(this.parameter);
      request.searchParams.delete('finish_page');
      if (this.mode === 'pops') request.searchParams.delete('filter.p.product_type');
      request.searchParams.set('section_id', this.root.dataset.sectionId);
      const themeId = this.root.dataset.finishTheme || window.Shopify?.theme?.id || this.committed.searchParams.get('preview_theme_id');
      // The current session already selected the rendered theme. A preview
      // redirect can collapse repeated facets and nested search options.
      request.searchParams.delete('preview_theme_id');
      const response = await fetch(request, { signal, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Collection request failed');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = document.querySelector('[data-section-id="' + CSS.escape(this.root.dataset.sectionId) + '"]');
      if (!section || (!this.isSearch && !section.querySelector(FORM) && !(this.nativeListing && section.hasAttribute('data-native-listing-root')))) throw new Error('Missing collection section');
      if (themeId && section.dataset.renderedThemeId !== String(themeId)) throw new Error('Unexpected collection theme');
      if (this.isSearch && section.dataset.searchTotal === '0' && !section.querySelector('.CollectionInner__Products')) {
        const empty = document.createElement('div');
        empty.className = 'CollectionInner__Products';
        section.append(empty);
      }
      if (this.mode === 'pops' ? section.querySelectorAll('.PopsCollection__group').length !== 3 : !section.querySelector('.CollectionInner__Products')) throw new Error('Missing collection results');
      return section;
    }

    async catalogue(url, section, signal) {
      const key = new URL(url);
      key.hash = ''; // Fragment identifies a position, not a different collection catalogue.
      key.searchParams.delete(this.parameter);
      key.searchParams.delete('finish_page');
      key.searchParams.delete('page');
      if (this.cache.has(key.href)) return this.cache.get(key.href);
      const wrapper = section.querySelector('.ProductListWrapper');
      const grid = wrapper?.querySelector('.ProductList')?.cloneNode(false);
      const count = this.isSearch ? section.dataset.searchTotal : wrapper?.dataset.finishTotal ?? section.querySelector('[data-filter-result-count]')?.dataset.filterResultCount;
      const total = Number(count);
      if (!Number.isInteger(total) || total < 0) throw new Error('Missing collection count');
      if (total && !grid) throw new Error('Missing collection grid');
      const perPage = Math.max(1, Number(this.root.dataset.finishPerPage || this.owner.settings?.perPage) || 16);
      const sourcePerPage = this.isSearch ? Number(this.root.dataset.searchSourcePerPage) || perPage : perPage;
      const pages = Math.ceil(total / sourcePerPage);
      const fragments = [section];
      let nextPage = 2;
      // Fetch every filtered Shopify page before grouping, including pairs split across pages.
      // Limit concurrency and commit the visible result only after completeness is verified.
      await Promise.all(Array.from({ length: Math.min(4, Math.max(0, pages - 1)) }, async () => {
        while (nextPage <= pages) {
          const page = nextPage++;
          const request = new URL(key);
          request.searchParams.set('page', page);
          fragments[page - 1] = await this.fetchSection(request, signal);
        }
      }));
      const cards = fragments.flatMap(fragment => [...fragment.querySelectorAll('.ProductList > .Grid__Cell')]
        .filter(cell => cell.querySelector('.ProductItem')));
      const handles = cards.map(cell => cell.querySelector('.ProductItem').dataset.handle);
      const sourceCount = this.isSearch ? fragments.reduce((sum, fragment) => sum +
        Number(fragment.querySelector('[data-search-record-count]')?.dataset.searchRecordCount || 0), 0) : cards.length;
      // Search can repeat the same record across native result pages. The model
      // pass removes it; completeness is measured by the raw page counts.
      if (sourceCount !== total || handles.some(handle => !handle) ||
        (!this.isSearch && new Set(handles).size !== cards.length)) throw new Error('Incomplete finish catalogue');
      const result = { cards, grid };
      this.cache.set(key.href, result);
      return result;
    }

    renderEssentials(section, catalogue, url) {
      const finish = this.finish(url);
      const matching = [];
      const seen = new Set();
      catalogue.cards.forEach(cell => {
        const card = cell.querySelector('.ProductItem');
        const switcher = card.querySelector('[data-product-card-switcher]');
        const sourceFinish = cell.dataset.collectionProductFinish?.trim() || switcher?.dataset.productCardFinish;
        if (finish !== 'all' && sourceFinish !== finish) return;
        // Only the validated pair emitted by the shared selector identifies one model.
        // Unpaired products and different colours retain their own cards and source order.
        const key = switcher?.dataset.productCardFinishPair || `product:${card.dataset.handle}`;
        if (seen.has(key)) return;
        seen.add(key);
        matching.push(cell);
      });
      const perPage = Math.max(1, Number(this.root.dataset.finishPerPage || this.owner.settings?.perPage) || 16);
      const pages = Math.max(1, Math.ceil(matching.length / perPage));
      const page = Math.max(1, Math.min(pages, Number(url.searchParams.get('finish_page') || url.searchParams.get('page')) || 1));
      url.searchParams.delete('page');
      if (page > 1) url.searchParams.set('finish_page', page);
      else url.searchParams.delete('finish_page');
      const wrapper = document.createElement('div');
      wrapper.className = 'ProductListWrapper';
      wrapper.dataset.finishGrouped = '';
      wrapper.dataset.modelCount = matching.length;
      if (matching.length) {
        const grid = catalogue.grid.cloneNode(false);
        grid.classList.remove('ProductList--LoadMore');
        grid.removeAttribute('data-nextpage');
        matching.slice((page - 1) * perPage, page * perPage).forEach(cell => grid.append(cell.cloneNode(true)));
        wrapper.append(grid);
      } else {
        const empty = document.createElement('p');
        empty.className = 'EmptyState';
        empty.textContent = this.root.dataset.finishEmpty || section.querySelector('.EmptyState, .UiState--empty')?.textContent || '';
        wrapper.append(empty);
      }
      const pagination = document.createElement('div');
      pagination.className = 'products__pagination';
      if (pages > 1) {
        const nav = document.createElement('nav');
        if (this.root.dataset.finishPagination) nav.setAttribute('aria-label', this.root.dataset.finishPagination);
        const list = document.createElement('ol');
        list.className = 'pagination';
        for (let index = 1; index <= pages; index += 1) {
          const item = document.createElement('li');
          if (index === page) {
            item.className = 'active';
            item.setAttribute('aria-current', 'page');
            item.textContent = index;
          } else {
            const link = document.createElement('a');
            const next = new URL(url);
            next.searchParams.set('finish_page', index);
            next.hash = '#collection-products';
            link.href = next;
            link.dataset.finishPage = index;
            link.textContent = index;
            item.append(link);
          }
          list.append(item);
        }
        nav.append(list);
        pagination.append(nav);
      }
      section.querySelector('.CollectionInner__Products').replaceChildren(wrapper, pagination);
      return matching.length;
    }

    filterPops(section, url) {
      const value = this.finish(url);
      const types = url.searchParams.getAll('filter.p.product_type');
      const products = Array.from(section.querySelectorAll('[data-pops-product]'));
      const groups = new Map();
      products.forEach(cell => {
        if (cell.closest('#pops-wearing')) {
          const switcher = cell.querySelector('[data-product-card-finish-pair]');
          if (switcher?.dataset.productCardFinishPair) {
            cell.dataset.popsModel = switcher.dataset.productCardFinishPair;
            cell.dataset.popsSourceFinish ||= switcher.dataset.productCardFinish;
          }
        }
        const independent = Boolean(cell.closest('#pops-sets'));
        const excluded = !independent && types.length && !cell.closest('#pops-charms') && !types.includes(cell.dataset.popsType);
        cell.hidden = !independent && Boolean(excluded || (value !== 'all' && cell.dataset.popsFinish !== value));
        if (cell.dataset.popsModel) {
          const key = cell.closest('.PopsCollection__group').getAttribute('aria-labelledby') + ':' + cell.dataset.popsModel;
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(cell);
        }
      });
      groups.forEach(group => {
        const pair = group.some(cell => cell.dataset.popsSourceFinish === 'gold') && group.some(cell => cell.dataset.popsSourceFinish === 'silver');
        if (!pair) return;
        const groupFinish = group[0].closest('#pops-sets') ? 'all' : value;
        const representative = group.find(cell => cell.dataset.popsSourceFinish === (groupFinish === 'all' ? 'gold' : groupFinish));
        group.forEach(cell => {
          const excluded = !cell.closest('#pops-sets') && types.length && !cell.closest('#pops-charms') && !types.includes(cell.dataset.popsType);
          cell.hidden = cell !== representative || Boolean(excluded);
        });
        if (groupFinish !== 'all' && representative?.dataset.popsFinish !== groupFinish) representative.dataset.requestedFinish = groupFinish;
      });
      section.querySelectorAll('.PopsCollection__group .ProductList').forEach(grid => {
        grid.querySelector('[data-filter-empty]')?.remove();
        if (grid.closest('#pops-sets') && !grid.querySelector('[data-pops-product]:not([hidden])')) {
          const empty = document.createElement('p');
          empty.dataset.filterEmpty = '';
          empty.className = 'EmptyState';
          empty.textContent = this.root.dataset.finishEmpty;
          grid.append(empty);
        }
      });
      this.updatePopsGroups(section);
      return products.filter(cell => !cell.hidden && !cell.closest('#pops-sets')).length;
    }

    updatePopsGroups(section) {
      const groups = [...section.querySelectorAll('.PopsCollection__group')];
      groups.forEach(group => {
        const empty = !group.querySelector('[data-pops-product]:not([hidden])');
        group.hidden = empty && group.id !== 'pops-sets';
        group.classList.toggle('is-empty', empty);
      });
      const first = groups.find(group => !group.hidden);
      groups.forEach(group => {
        const firstVisible = group === first;
        group.classList.toggle('is-first-visible', firstVisible);
        group.querySelector('.PopsCollection__groupTitle')?.classList.toggle('u-visually-hidden', firstVisible);
      });
      // Keep every visible navigation link pointing to a visible result section.
      section.querySelectorAll('.PopsCollection__stepLink, .PopsCollection__actions a[href^="#"]').forEach(anchor => {
        const original = anchor.dataset.popsTarget || anchor.getAttribute('href');
        anchor.dataset.popsTarget = original;
        const target = groups.find(group => `#${group.id}` === original);
        if (!target) return;
        const step = anchor.closest('.PopsCollection__steps > li');
        if (step) step.hidden = target.hidden;
        else anchor.setAttribute('href', target.hidden && first ? `#${first.id}` : original);
      });
      const steps = section.querySelector('.PopsCollection__steps');
      if (steps) {
        const visibleSteps = steps.querySelectorAll(':scope > li:not([hidden])').length;
        steps.classList.toggle('is-single-step', visibleSteps === 1);
        steps.closest('.PopsCollection__how').hidden = visibleSteps === 0;
      }
    }

    render(section) {
      this.nativeListing?.stop();
      const selectors = this.mode === 'pops'
        ? ['#pops-charms .ProductList', '#pops-wearing .ProductList', '.PopsCollection__group--sets .ProductList', '.PopsCollection .products__pagination']
        : ['.CollectionInner__Products'];
      // Validate the complete response before touching the visible results.
      selectors.forEach(selector => {
        if (this.root.querySelector(selector) && !section.querySelector(selector)) throw new Error('Missing result fragment');
      });
      selectors.concat(['.CollectionToolbar__Group', '#collection-filters-drawer-form', '#collection-filters-sidebar-form']).forEach(selector => {
        const current = this.root.querySelector(selector);
        const replacement = section.querySelector(selector);
        if (!current || !replacement) return;
        current.querySelectorAll('.swiper').forEach(node => node.swiper?.destroy(true, true));
        current.replaceChildren(...Array.from(replacement.childNodes));
        if (selector === '.CollectionToolbar__Group') current.className = replacement.className;
      });
      this.owner.collectionInnerElement = this.root.querySelector('.CollectionInner__Products');
      this.owner.refreshFilterProducts?.();
      this.root.querySelectorAll('[data-requested-finish]').forEach(cell => {
        cell.querySelector('.ProductCardSwitcher__finish:has(.ProductCardSwitcher__swatch--' + cell.dataset.requestedFinish + ')')?.click();
        delete cell.dataset.requestedFinish;
      });
      const grid = this.root.querySelector('#pops-wearing .ProductList');
      const visible = Array.from(grid?.querySelectorAll('[data-pops-product]') || []).filter(cell => !cell.hidden);
      grid?.classList.toggle('Grid--center', Boolean(visible.length && Math.abs(visible[0].getBoundingClientRect().top - visible[visible.length - 1].getBoundingClientRect().top) < 1));
      if (this.mode === 'pops') this.updatePopsGroups(this.root);
      document.dispatchEvent(new CustomEvent('collection-finish:updated', { detail: { root: this.root } }));
    }

    async apply(input, options = {}) {
      this.cancelPreview();
      const url = new URL(input, location.href);
      if (this.mode === 'pops') url.searchParams.delete('filter.p.product_type');
      preservePreviewFacets(url);
      // Preserve the position visible at confirmation, including layout changes since opening.
      if (options.close && this.drawer?.getAttribute('aria-hidden') === 'false') this.openPosition = window.scrollY;
      let position = this.openPosition ?? window.scrollY;
      if (this.demo) {
        this.committed = url;
        this.sync(url, 3 - Number(this.finish(url) !== 'all') - Number(url.searchParams.has('design_system_available')));
        if (options.close) this.owner.filterDrawer.close();
        this.restorePosition(position);
        return;
      }
      if (url.pathname !== this.committed.pathname || url.origin !== location.origin) return;
      const token = ++this.operation;
      this.controller?.abort();
      this.nativeListing?.stop();
      const controller = this.controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      const messages = this.root.querySelector('[data-filter-messages]')?.dataset;
      this.status(messages?.loading || this.root.dataset.finishLoading, true);
      document.dispatchEvent(new CustomEvent('theme:loading:start'));
      try {
        // Model pagination is independent of Shopify record pagination.
        const request = new URL(url);
        if (this.groupFinishes) request.searchParams.delete('page');
        let section = await this.fetchSection(request, controller.signal);
        const result = section.querySelector('[data-collection-model-count]') || section.querySelector('[data-filter-result-count]');
        const resultCount = result?.dataset.collectionModelCount ?? result?.dataset.filterResultCount;
        let count = resultCount !== undefined && /^\d+$/.test(resultCount) ? Number(resultCount) : undefined;
        if (this.mode === 'pops') count = this.filterPops(section, url);
        else if (this.groupFinishes) count = this.renderEssentials(section, await this.catalogue(url, section, controller.signal), url);
        let nativePages;
        if (this.nativeListing) {
          const native = await this.nativeListing.prepare(url, section, controller.signal);
          section = native.section;
          count = native.count;
          nativePages = native.pages;
        }
        if (token !== this.operation) return;
        if (controller.signal.aborted) throw new Error('Collection request timed out');
        if (options.initial) position = window.scrollY;
        this.render(section);
        this.committed = url;
        this.nativePages = nativePages;
        this.nativeListing?.install(nativePages);
        if (!options.history && !options.initial && url.href !== location.href) history.pushState({ collectionFilters: true }, '', url);
        if (options.initial && url.href !== location.href) history.replaceState({ collectionFilters: true }, '', url);
        this.sync(url, count);
        this.status();
        // Closing a successful apply must not restore the position captured when opening.
        if (options.close) {
          this.openPosition = null;
          this.owner.filterDrawer?.close();
        }
        if (!options.initial || options.pagination || url.hash === '#collection-products') requestAnimationFrame(() => CollectionFilters.scrollToProducts(this.root));
        else this.restorePosition(position);
      } catch (error) {
        if (token !== this.operation) return;
        this.nativeListing?.install(this.nativePages);
        // Keep the last successful results and draft; applying again retries.
        this.status(messages?.error || this.root.dataset.finishError);
        if (options.history) history.replaceState({ collectionFilters: true }, '', this.committed);
        this.restorePosition(position);
      } finally {
        window.clearTimeout(timeout);
        if (token === this.operation) document.dispatchEvent(new CustomEvent('theme:loading:end'));
      }
    }
  }
  window.CollectionFilters = CollectionFilters;
})();
